import { supabase } from '../supabase';

// Every lookup below throws on a Supabase error instead of returning an empty result.
// supabase-js resolves (never rejects) on a network/gateway failure — `{ data: null, error }` —
// so ignoring `error` made "the request failed" indistinguishable from "nothing to cascade to",
// and a forward-week cascade would be skipped with no trace.

/**
 * Returns the client's current_week for this program, or null if there's no
 * single active assignment (e.g. an unassigned master template) — nothing to
 * cascade to in that case. Throws if the lookup itself fails.
 */
export const getActiveAssignmentCurrentWeek = async (programId) => {
  const { data, error } = await supabase
    .from('program_assignments')
    .select('current_week')
    .eq('program_id', programId)
    .eq('is_active', true)
    .maybeSingle();
  // PGRST116: more than one active assignment, so there's no single current week to cascade
  // from. That's a data shape, not an outage — same "no cascade" outcome this had before errors
  // were checked, rather than blocking the coach from saving.
  if (error && error.code === 'PGRST116') return null;
  if (error) throw error;
  return data?.current_week ?? null;
};

/**
 * Returns program_days ids with the given day_number, from currentWeek
 * onwards, for the given program. Returns [] if nothing found — callers
 * must guard against empty arrays before using .in(). Throws if a lookup fails.
 */
export const getForwardDayIds = async (programId, dayNumber, currentWeek) => {
  const { data: weeks, error: weeksErr } = await supabase
    .from('program_weeks')
    .select('id')
    .eq('program_id', programId)
    .gte('week_number', currentWeek);
  if (weeksErr) throw weeksErr;
  const weekIds = (weeks || []).map(w => w.id);
  if (weekIds.length === 0) return [];

  const { data: days, error: daysErr } = await supabase
    .from('program_days')
    .select('id')
    .eq('day_number', dayNumber)
    .in('week_id', weekIds);
  if (daysErr) throw daysErr;
  return (days || []).map(d => d.id);
};

/**
 * Returns exercise_blocks ids with the given block_order among the supplied
 * day ids. Throws if the lookup fails.
 */
export const getMatchingBlockIds = async (blockOrder, dayIds) => {
  if (dayIds.length === 0) return [];
  const { data: blocks, error } = await supabase
    .from('exercise_blocks')
    .select('id')
    .eq('block_order', blockOrder)
    .in('day_id', dayIds);
  if (error) throw error;
  return (blocks || []).map(b => b.id);
};

// The runner below is for a caller whose primary write has ALREADY landed and that can't use the
// single-request functions further down (ProgramBuilder's whole-tree save). It never throws: it
// resolves `{ ok: true, ... }` (the ids list is [] when there is nothing to cascade to) or
// `{ ok: false, error }`, so the caller can tell the coach the cascade didn't land instead of
// reporting a clean save. Each cascade write only matches rows that still hold the old value,
// so calling it again after a failure is safe.

/**
 * Applies `write(blockIds)` to block_items in the client's current + future weeks at the given
 * block position. `write` must return the Supabase query (it resolves to `{ error }`).
 * Resolves `{ ok: true, blockIds }` or `{ ok: false, error }`.
 */
export const cascadeToForwardBlocks = async ({ programId, currentWeek, dayNumber, blockOrder, write }) => {
  try {
    const dayIds = await getForwardDayIds(programId, dayNumber, currentWeek);
    const blockIds = await getMatchingBlockIds(blockOrder, dayIds);
    if (blockIds.length > 0) {
      const { error } = await write(blockIds);
      if (error) throw error;
    }
    return { ok: true, blockIds };
  } catch (error) {
    return { ok: false, error };
  }
};

// ── Single-request cascades (migration 057) ─────────────────────────────────────────────
// Edit / swap / delete an exercise row AND apply the same change to the client's current and
// later weeks in ONE request and ONE database transaction, instead of five sequential requests
// (write, weeks, days, blocks, cascade write). Atomic: the shown row and every later-week row
// change together or not at all, so there is no "saved here but not there" state to recover from.
// The functions run as the signed-in user, so row-level security applies exactly as it does to
// direct table writes. Each resolves `{ ok: true, ... }` or `{ ok: false, error }` and never throws.
//
// `matchExerciseId` is the exercise the row held BEFORE the change: only later-week rows that
// still hold it are touched, which also makes repeating a call after a lost response harmless.

// How long to wait before each retry of a transient cascade failure. Exported (and mutable) so
// tests can run the retry path without sleeping; production code should not touch it.
export const CASCADE_RETRY_DELAYS_MS = [400, 1200];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Is this a failure worth retrying? Only transport-level ones: a dropped connection, or a
// gateway 5xx (Cloudflare 520/522, a 504) that never reached PostgREST. Those carry no
// Access-Control-Allow-Origin header, which is why the browser reports them as CORS errors.
// A real database error is deterministic and always carries a `code` (22023, P0002, 42501,
// PGRST202), so retrying it would just fail again more slowly.
const isTransient = (error, status) => {
  if (error?.code) return false;
  if (status >= 500) return true;
  return /failed to fetch|networkerror|load failed|network request failed|timed? ?out|aborted/i
    .test(error?.message || '');
};

// Retrying is safe for every function here, including the nastiest case — the transaction
// committed but the response was lost. Each call is ONE atomic transaction, and its cascade
// writes only match rows that still hold the old value, so a repeat either finds the work
// already done (and changes nothing) or finishes it. Deletes treat an already-deleted row as
// success for the same reason. This is why the retry lives here and NOT around the
// add-exercise inserts, which would duplicate rows.
// Which migration creates a function, for the "has not been applied yet" message. 057 unless listed.
const MIGRATION_OF = {
  cascade_update_block_rest: '062',
  editor_update_block_item: '085', editor_delete_block_item: '085', editor_update_block_rest: '085', editor_add_exercise: '085',
  editor_add_day: '087',
};

const callCascadeRpc = async (fn, args) => {
  for (let attempt = 0; ; attempt++) {
    const { data, error, status } = await supabase.rpc(fn, args);
    if (!error) return { ok: true, data };

    // PGRST202: the function isn't in the schema cache, i.e. its migration hasn't been applied.
    // Say so plainly instead of surfacing a cryptic PostgREST message to the coach.
    if (error.code === 'PGRST202') {
      return { ok: false, error: { ...error, message: `Database migration ${MIGRATION_OF[fn] || '057'} has not been applied yet (missing ${fn}).` } };
    }

    if (attempt < CASCADE_RETRY_DELAYS_MS.length && isTransient(error, status)) {
      console.warn(
        `${fn}: transient failure (${error.message || status}) — retrying ${attempt + 2}/${CASCADE_RETRY_DELAYS_MS.length + 1}`
      );
      // Up to half as long again, at random: editors who failed together do not retry together
      // (Codex's review 023, R6)
      const delay = CASCADE_RETRY_DELAYS_MS[attempt];
      await sleep(delay + Math.round(Math.random() * delay * 0.5));
      continue;
    }
    return { ok: false, error };
  }
};

/**
 * Edit or swap a block_items row and cascade it. `patch` may hold `exercise_id`, `sets`, `reps`.
 * Resolves `{ ok: true, blockIds }` (the later-week blocks that were targeted) or `{ ok: false, error }`.
 */
export const updateBlockItemCascade = async ({ itemId, matchExerciseId, patch, programId, currentWeek, dayNumber, blockOrder }) => {
  const res = await callCascadeRpc('cascade_update_block_item', {
    p_item_id: itemId,
    p_match_exercise_id: matchExerciseId,
    p_patch: patch,
    p_program_id: programId,
    p_current_week: currentWeek,
    p_day_number: dayNumber,
    p_block_order: blockOrder,
  });
  return res.ok ? { ok: true, blockIds: res.data?.cascade_block_ids ?? [] } : res;
};

/**
 * Set the rest time of a block and cascade it (migration 062). Rest belongs to the BLOCK
 * (exercise_blocks.rest_seconds), not to an exercise row, which is why it has its own function:
 * sending `rest_seconds` through updateBlockItemCascade is rejected, since block_items has no such
 * column. `matchExerciseId` is an exercise the block holds; later-week blocks at the same position
 * are changed only if they still hold it.
 * Resolves `{ ok: true, blockIds }` (the later-week blocks that were changed) or `{ ok: false, error }`.
 */
export const updateBlockRestCascade = async ({ blockId, matchExerciseId, restSeconds, programId, currentWeek, dayNumber, blockOrder }) => {
  const res = await callCascadeRpc('cascade_update_block_rest', {
    p_block_id: blockId,
    p_match_exercise_id: matchExerciseId,
    p_rest_seconds: restSeconds,
    p_program_id: programId,
    p_current_week: currentWeek,
    p_day_number: dayNumber,
    p_block_order: blockOrder,
  });
  return res.ok ? { ok: true, blockIds: res.data?.cascade_block_ids ?? [] } : res;
};

/** Delete a block_items row and the same exercise at the same block position in later weeks. */
export const deleteBlockItemCascade = async ({ itemId, matchExerciseId, programId, currentWeek, dayNumber, blockOrder }) => {
  const res = await callCascadeRpc('cascade_delete_block_item', {
    p_item_id: itemId,
    p_match_exercise_id: matchExerciseId,
    p_program_id: programId,
    p_current_week: currentWeek,
    p_day_number: dayNumber,
    p_block_order: blockOrder,
  });
  return res.ok ? { ok: true, blockIds: res.data?.cascade_block_ids ?? [] } : res;
};

/**
 * Legacy (non-block) equivalent of updateBlockItemCascade for program_exercises.
 * Resolves `{ ok: true, dayIds }` or `{ ok: false, error }`.
 */
export const updateProgramExerciseCascade = async ({ peId, matchExerciseId, patch, programId, currentWeek, dayNumber }) => {
  const res = await callCascadeRpc('cascade_update_program_exercise', {
    p_pe_id: peId,
    p_match_exercise_id: matchExerciseId,
    p_patch: patch,
    p_program_id: programId,
    p_current_week: currentWeek,
    p_day_number: dayNumber,
  });
  return res.ok ? { ok: true, dayIds: res.data?.cascade_day_ids ?? [] } : res;
};

/** Legacy (non-block) equivalent of deleteBlockItemCascade for program_exercises. */
export const deleteProgramExerciseCascade = async ({ peId, matchExerciseId, programId, currentWeek, dayNumber }) => {
  const res = await callCascadeRpc('cascade_delete_program_exercise', {
    p_pe_id: peId,
    p_match_exercise_id: matchExerciseId,
    p_program_id: programId,
    p_current_week: currentWeek,
    p_day_number: dayNumber,
  });
  return res.ok ? { ok: true, dayIds: res.data?.cascade_day_ids ?? [] } : res;
};

// ── The editor's functions (migration 085; Codex's review 023, R1 to R3) ─────────────────────────
// Each takes only the row and the exercise the coach saw on it; the program, the client's current week,
// the day and the block position are worked out in the database. A refusal carries a hint the editor
// reads (editorRefusal): 'stale_week' (not the client's current week), 'changed' (changed elsewhere),
// 'not_assigned' (no single active client). Each resolves `{ ok: true, blockIds, week }` or `{ ok: false, error }`.

/** What a refused editor call means, for the coach; null when it is not one of the editor's refusals. */
export const editorRefusal = (error) => ({
  stale_week: 'The client has moved on to another week since this opened. Showing their current week now.',
  changed: 'This exercise was changed somewhere else meanwhile. Showing it as it is now.',
  not_assigned: 'This program has no single active client, so it cannot be edited here.',
  from_next_week: 'Days are added from the client\'s next week. Showing the program as it is now.',
  week_not_made: 'That week has not been made yet. Showing the program as it is now.',
  anchor_missing: 'A later week does not have the day you chose to put it after. Choose another place, or the start or end.',
  legacy_format: 'Days can be added only to programs made of blocks; this one is in the older format.',
}[error?.hint] || null);

const editorResult = (res) => (res.ok
  ? { ok: true, blockIds: res.data?.cascade_block_ids ?? [], week: res.data?.week ?? null, data: res.data }
  : res);

/** Edit or swap an exercise row in the client's current week, and the same row in later weeks. */
export const editorUpdateBlockItem = async ({ itemId, seenExerciseId, patch }) => editorResult(
  await callCascadeRpc('editor_update_block_item', { p_item_id: itemId, p_seen_exercise_id: seenExerciseId, p_patch: patch }));

/** Remove an exercise row from the client's current week and later weeks; repeating it is harmless. */
export const editorDeleteBlockItem = async ({ itemId, seenExerciseId }) => editorResult(
  await callCascadeRpc('editor_delete_block_item', { p_item_id: itemId, p_seen_exercise_id: seenExerciseId }));

/** Set a block's rest in the client's current week and later weeks. */
export const editorUpdateBlockRest = async ({ blockId, seenExerciseId, restSeconds }) => editorResult(
  await callCascadeRpc('editor_update_block_rest', { p_block_id: blockId, p_seen_exercise_id: seenExerciseId, p_rest_seconds: restSeconds }));

/**
 * Add an exercise to a day of the client's current week and the same day of every later week, in one
 * transaction. `requestId` makes a retry safe: the same id returns the first answer instead of adding it
 * again, which is why this one may be retried too.
 */
export const editorAddExercise = async ({ dayId, exerciseId, sets, reps, requestId }) => {
  const res = await callCascadeRpc('editor_add_exercise', {
    p_day_id: dayId, p_exercise_id: exerciseId, p_sets: sets, p_reps: String(reps), p_request_id: requestId,
  });
  return res.ok ? { ok: true, blockIds: res.data?.block_ids ?? [], week: res.data?.week ?? null } : res;
};

/**
 * Add an empty, named day at `position` (1 = first) in week `fromWeek` and every later week already made
 * (migration 087). Never the client's current week or earlier. `requestId` makes a retry safe.
 */
export const editorAddDay = async ({ programId, fromWeek, place, afterSlot = null, name, requestId }) => {
  const res = await callCascadeRpc('editor_add_day', {
    p_program_id: programId, p_from_week: fromWeek, p_place: place, p_after_slot: afterSlot, p_name: name, p_request_id: requestId,
  });
  return res.ok ? { ok: true, dayIds: res.data?.day_ids ?? [], fromWeek: res.data?.from_week ?? fromWeek } : res;
};
